import { Directive, forwardRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  AbstractControl,
  AsyncValidator,
  NG_ASYNC_VALIDATORS,
  ValidationErrors,
} from '@angular/forms';
import { Observable, catchError, map, of, switchMap, timer } from 'rxjs';

type DnsResponse = {
  Status?: number;
  Answer?: unknown[];
};

const PROVIDER_TYPOS: Record<string, string> = {
  'gmail.it': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'hotmai.com': 'hotmail.com',
};

@Directive({
  selector: '[samiRealEmail]',
  standalone: true,
  providers: [
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => RealEmailValidatorDirective),
      multi: true,
    },
  ],
})
export class RealEmailValidatorDirective implements AsyncValidator {
  constructor(private http: HttpClient) {}

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    const email = String(control.value || '').trim().toLowerCase();
    const domain = email.split('@')[1];

    // I validator required/email mostrano gli errori di sintassi.
    if (!domain || !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)) return of(null);

    const suggestion = PROVIDER_TYPOS[domain];
    if (suggestion) return of({ providerTypo: { suggestion } });

    return timer(350).pipe(
      switchMap(() => this.lookup(domain, 'MX')),
      switchMap(mx => {
        if (this.hasAnswer(mx)) return of(null);
        if (mx.Status === 3) return of({ emailDomainMissing: true });

        // RFC: in assenza di MX un dominio con record A può comunque ricevere posta.
        return this.lookup(domain, 'A').pipe(
          map(a => this.hasAnswer(a) ? null : { emailDomainMissing: true }),
        );
      }),
      // Problemi di rete o DNS temporanei non devono bloccare un contatto reale.
      catchError(() => of(null)),
    );
  }

  private lookup(domain: string, type: 'MX' | 'A'): Observable<DnsResponse> {
    return this.http.get<DnsResponse>('https://dns.google/resolve', {
      params: { name: domain, type },
    });
  }

  private hasAnswer(response: DnsResponse): boolean {
    return response.Status === 0 && Array.isArray(response.Answer) && response.Answer.length > 0;
  }
}
